import {useCallback, useEffect, useState} from 'react'
import {useRouter} from 'next/navigation'
import useSWR from 'swr'

// Backup key for localStorage - using a more specific name to avoid conflicts
const JWT_BACKUP_KEY = 'cffc_jwt_backup'
const USER_BACKUP_KEY = 'cffc_user_backup'
const SESSION_TIMESTAMP_KEY = 'cffc_session_timestamp'

// https://github.com/vercel/next.js/blob/canary/examples/with-iron-session/lib/useUser.js
export default function useUser({
                                    redirectTo = '',
                                    redirectIfFound = false,
                                } = {}) {

    // https://swr.vercel.app/docs/getting-started#make-it-reusable
    // https://swr.vercel.app/docs/mutation#bound-mutate
    // https://swr.vercel.app/docs/getting-started
    const router = useRouter()
    const [isLoading, setIsLoading] = useState(true)
    const [sessionChecks, setSessionChecks] = useState(0)
    const [backupUsed, setBackupUsed] = useState(false)

    // Store JWT and essential user data in localStorage for backup
    const storeBackup = (data) => {
        if (typeof window === 'undefined' || !data || !data.isLoggedIn) return

        try {
            // Store JWT token separately
            if (data.jwt) {
                localStorage.setItem(JWT_BACKUP_KEY, data.jwt)
                console.log('JWT token backed up to localStorage')
            }

            // Store essential user data
            const userData = {
                id: data.id,
                email: data.email,
                username: data.username,
                firstname: data.firstname,
                lastname: data.lastname,
                isLoggedIn: true,
            }
            localStorage.setItem(USER_BACKUP_KEY, JSON.stringify(userData))

            // Update timestamp
            localStorage.setItem(SESSION_TIMESTAMP_KEY, Date.now().toString())
            console.log('User data backed up to localStorage')
        } catch (err) {
            console.error('Failed to backup user data to localStorage:', err)
        }
    }

    // Enhanced fetcher with error handling, retry logic, and localStorage backup
    const fetcher = async (...args: [RequestInfo | URL, RequestInit?]) => {
        try {
            console.log('Fetching user data...')

            // Add JWT from localStorage to request if available
            const requestInit = args[1] || {}
            const jwt = localStorage.getItem(JWT_BACKUP_KEY)
            if (jwt && !requestInit.headers) {
                requestInit.headers = {
                    'X-JWT-Backup': jwt,
                }
                args[1] = requestInit
                console.log('Added JWT from localStorage to request headers')
            }

            const res = await fetch(...args)
            if (!res.ok) {
                console.error(`Failed to fetch user: ${res.statusText}`)

                // If server request fails, try to use backup data
                if (typeof window !== 'undefined') {
                    const storedJwt = localStorage.getItem(JWT_BACKUP_KEY)
                    const storedUserData = localStorage.getItem(USER_BACKUP_KEY)
                    const timestamp = localStorage.getItem(SESSION_TIMESTAMP_KEY)

                    if (storedJwt && storedUserData && timestamp) {
                        const now = Date.now()
                        const age = now - parseInt(timestamp, 10)

                        // Use backup if it's less than 24 hours old
                        if (age < 24 * 60 * 60 * 1000) {
                            console.log('Using localStorage backup data')
                            setBackupUsed(true)
                            return {
                                ...JSON.parse(storedUserData),
                                jwt: storedJwt,
                                fromBackup: true,
                            }
                        }
                    }
                }

                throw new Error(`Failed to fetch user: ${res.statusText}`)
            }

            const data = await res.json()
            console.log('User data fetched successfully:', data)

            // Backup data to localStorage
            if (data.isLoggedIn) {
                storeBackup(data)
            } else {
                // Clear backups if user is not logged in
                if (typeof window !== 'undefined') {
                    localStorage.removeItem(JWT_BACKUP_KEY)
                    localStorage.removeItem(USER_BACKUP_KEY)
                    localStorage.removeItem(SESSION_TIMESTAMP_KEY)
                }
            }

            return data
        } catch (error) {
            console.error('Error fetching user data:', error)

            // Try to use backup data if available
            if (typeof window !== 'undefined') {
                const storedJwt = localStorage.getItem(JWT_BACKUP_KEY)
                const storedUserData = localStorage.getItem(USER_BACKUP_KEY)

                if (storedJwt && storedUserData) {
                    console.log('Using localStorage backup after fetch error')
                    setBackupUsed(true)
                    return {
                        ...JSON.parse(storedUserData),
                        jwt: storedJwt,
                        fromBackup: true,
                    }
                }
            }

            throw error
        }
    }

    // SWR configuration for better session persistence
    const {data: user, mutate: mutateUser, error} = useSWR('/api/user', fetcher, {
        // Revalidate on focus - enables checking session when tab is re-focused
        revalidateOnFocus: true,
        // Always revalidate when component mounts
        revalidateOnMount: true,
        // Revalidate if data might be stale
        revalidateIfStale: true,
        // Shorter cache time to ensure fresh session checks
        dedupingInterval: 3000, // 3 seconds
        // Use previous data while revalidating
        keepPreviousData: true,
        // Loading handlers
        onLoadingSlow: () => setIsLoading(true),
        onSuccess: (data) => {
            setIsLoading(false)
            setSessionChecks(prev => prev + 1)
            // If we successfully loaded data, update backup
            if (data && data.isLoggedIn) {
                storeBackup(data)
            }
        },
        // Retry configuration for failed requests
        errorRetryCount: 3,
        errorRetryInterval: 2000,
        // Error handler
        onError: (err) => {
            console.error('SWR error in useUser:', err)
            setIsLoading(false)
        },
    })

    // Force revalidation function for manual session checks
    const validateSession = useCallback(async () => {
        console.log('Manually validating session...')
        try {
            // Check localStorage for JWT as backup
            let options = {}
            const jwt = localStorage.getItem(JWT_BACKUP_KEY)
            if (jwt) {
                options = {
                    headers: {'X-JWT-Backup': jwt},
                }
            }

            await mutateUser(async () => {
                try {
                    const res = await fetch('/api/user', options)
                    if (!res.ok) throw new Error('Session validation failed')
                    const data = await res.json()
                    if (data.isLoggedIn) {
                        storeBackup(data)
                    }
                    return data
                } catch (err) {
                    console.error('Session validation request failed:', err)

                    // Use backup if available
                    const storedUserData = localStorage.getItem(USER_BACKUP_KEY)
                    if (jwt && storedUserData) {
                        console.log('Using localStorage backup after validation failure')
                        return {
                            ...JSON.parse(storedUserData),
                            jwt,
                            fromBackup: true,
                        }
                    }
                    throw err
                }
            })
        } catch (err) {
            console.error('Manual session validation failed:', err)
        }
    }, [mutateUser])

    // Check for backup data on first load
    useEffect(() => {
        if (typeof window === 'undefined' || backupUsed) return

        const checkBackup = async () => {
            const storedJwt = localStorage.getItem(JWT_BACKUP_KEY)
            const storedUserData = localStorage.getItem(USER_BACKUP_KEY)

            if (storedJwt && storedUserData && (!user || !user.isLoggedIn)) {
                console.log('Initial check - using backup data')
                try {
                    // Try to validate with server first
                    const res = await fetch('/api/user', {
                        headers: {'X-JWT-Backup': storedJwt},
                    })

                    if (!res.ok) {
                        // If server validation fails but backup is recent, use it
                        const timestamp = localStorage.getItem(SESSION_TIMESTAMP_KEY)
                        if (timestamp) {
                            const age = Date.now() - parseInt(timestamp, 10)
                            if (age < 60 * 60 * 1000) { // 1 hour
                                console.log('Using backup data (server validation failed)')
                                setBackupUsed(true)
                                const parsedUserData = JSON.parse(storedUserData)
                                await mutateUser({
                                    ...parsedUserData,
                                    jwt: storedJwt,
                                    fromBackup: true,
                                }, false)
                            }
                        }
                    }
                } catch (err) {
                    console.error('Error checking backup:', err)
                }
            }
        }

        checkBackup()
    }, [user, mutateUser, backupUsed])

    // Session validation on mount
    useEffect(() => {
        // Initial validation
        validateSession()

        // Set up periodic session validation
        const intervalId = setInterval(() => {
            validateSession()
        }, 60000) // Check every minute

        return () => clearInterval(intervalId)
    }, [validateSession])

    // Handle page visibility changes
    useEffect(() => {
        if (typeof window === 'undefined') return

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                console.log('Page became visible, validating session...')
                validateSession()
            }
        }

        document.addEventListener('visibilitychange', handleVisibilityChange)
        window.addEventListener('focus', validateSession)

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange)
            window.removeEventListener('focus', validateSession)
        }
    }, [validateSession])

    // Handle redirection logic
    useEffect(() => {
        // Update loading state based on user data
        if (user !== undefined) {
            setIsLoading(false)
        }

        // if no redirect needed, just return (example: already on /dashboard)
        // if user data not yet there (fetch in progress, logged in or not) then don't do anything yet
        if (!redirectTo || user === undefined) return

        if (
            // If redirectTo is set, redirect if the user was not found.
            ((redirectTo != '') && !redirectIfFound && !user?.isLoggedIn) ||
            ((redirectTo != '') && !redirectIfFound && user?.error) ||
            // If redirectIfFound is also set, redirect if the user was found
            (redirectIfFound && user?.isLoggedIn)
        ) {
            router.push(redirectTo)
        }
    }, [user, redirectIfFound, redirectTo, router])

    return {
        user,
        isLoading,
        mutateUser,
        validateSession,
        sessionChecks,
        error,
    }
}
